// Devices and Phone Numbers pages (mig 0015): registry lists, detail with
// linked accounts, phone ↔ device reassignment, and change history showing
// who made each change.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { devices, lookups, phoneNumbers } from '@/lib/api';
import { useMe } from '@/hooks/useMe';
import { PHONE_LINE_TYPES } from '@frt/shared-types';
import type {
  AssetHistoryEntry,
  AssetLinkedProfile,
  AssetListQuery,
  DeviceRow,
  PhoneNumberDetail,
} from '@frt/shared-types';
import { DeviceFormDialog, DevicePicker, PhoneFormDialog } from './asset-forms';

type ActiveFilter = NonNullable<AssetListQuery['active']>;

function useCanEdit(): boolean {
  const { data: me } = useMe();
  return me?.role === 'admin' || me?.role === 'senior';
}

// ===========================================================================
// Lists
// ===========================================================================

export function DevicesListPage() {
  const canEdit = useCanEdit();
  const [q, setQ] = useState('');
  const [active, setActive] = useState<ActiveFilter>('true');
  const [creating, setCreating] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['devices', { q, active }],
    queryFn: () => devices.list({ q: q || undefined, active }),
  });
  const rows = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold text-slate-900">Devices</h1>
        {canEdit && <Button onClick={() => setCreating(true)}>+ New device</Button>}
      </div>
      <ListFilters q={q} onQ={setQ} active={active} onActive={setActive} placeholder="Device ID, make or model" />
      <Card>
        <CardBody className="overflow-x-auto p-0">
          {isLoading ? (
            <p className="p-4 text-sm text-slate-500">Loading…</p>
          ) : error ? (
            <p className="p-4 text-sm text-red-600">{(error as Error).message}</p>
          ) : rows.length === 0 ? (
            <p className="p-4 text-sm text-slate-500">No devices.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-500">
                <tr>
                  <Th>Device ID</Th>
                  <Th>Manufacturer</Th>
                  <Th>Model</Th>
                  <Th>Ownership</Th>
                  <Th>Custodian</Th>
                  <Th>Phone Numbers</Th>
                  <Th>Profiles</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id} className={`border-t border-slate-100 ${d.active ? '' : 'text-slate-400'}`}>
                    <Td mono>
                      <Link to="/assets/devices/$id" params={{ id: String(d.id) }} className="text-brand-700 hover:underline">
                        {d.device_identifier}
                      </Link>
                      {!d.active && ' (retired)'}
                    </Td>
                    <Td>{d.make ?? <Missing />}</Td>
                    <Td>{d.model ?? <Missing />}</Td>
                    <Td>{d.ownership_name ?? <Missing />}</Td>
                    <Td>{d.owner_display ?? <Missing />}</Td>
                    <Td mono>{d.assigned_phones.map((p) => p.display).join(', ') || '—'}</Td>
                    <Td>{d.linked_profile_count}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
      <DeviceFormDialog open={creating} onClose={() => setCreating(false)} onSaved={() => undefined} />
    </div>
  );
}

export function PhoneNumbersListPage() {
  const canEdit = useCanEdit();
  const [q, setQ] = useState('');
  const [active, setActive] = useState<ActiveFilter>('true');
  const [creating, setCreating] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['phone-numbers', { q, active }],
    queryFn: () => phoneNumbers.list({ q: q || undefined, active }),
  });
  const rows = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold text-slate-900">Phones</h1>
        {canEdit && <Button onClick={() => setCreating(true)}>+ New phone</Button>}
      </div>
      <ListFilters q={q} onQ={setQ} active={active} onActive={setActive} placeholder="Number or device ID" />
      <Card>
        <CardBody className="overflow-x-auto p-0">
          {isLoading ? (
            <p className="p-4 text-sm text-slate-500">Loading…</p>
          ) : error ? (
            <p className="p-4 text-sm text-red-600">{(error as Error).message}</p>
          ) : rows.length === 0 ? (
            <p className="p-4 text-sm text-slate-500">No phones.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-500">
                <tr>
                  <Th>Phone Number</Th>
                  <Th>Subscriber</Th>
                  <Th>Carrier</Th>
                  <Th>Carrier Type</Th>
                  <Th>Test Status</Th>
                  <Th>Usage</Th>
                  <Th>Device</Th>
                  <Th>Last Port</Th>
                  <Th>Profiles</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className={`border-t border-slate-100 ${p.active ? '' : 'text-slate-400'}`}>
                    <Td mono>
                      <Link to="/assets/phones/$id" params={{ id: String(p.id) }} className="text-brand-700 hover:underline">
                        {p.display}
                      </Link>
                      {!p.active && ' (retired)'}
                    </Td>
                    <Td>{p.owner_display ?? <Missing />}</Td>
                    <Td>{p.provider_name ?? <Missing />}</Td>
                    <Td>{p.carrier_type_name ?? <Missing />}</Td>
                    <Td>{p.test_status_name ?? <Missing />}</Td>
                    <Td>{p.usage_status_name ?? <Missing />}</Td>
                    <Td>
                      {p.device_id ? (
                        <Link to="/assets/devices/$id" params={{ id: String(p.device_id) }} className="text-brand-700 hover:underline">
                          {p.device_display}
                        </Link>
                      ) : (
                        'No device'
                      )}
                    </Td>
                    <Td>{p.last_port_date ?? '—'}</Td>
                    <Td>{p.linked_profile_count}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
      <PhoneFormDialog open={creating} onClose={() => setCreating(false)} onSaved={() => undefined} />
    </div>
  );
}

// ===========================================================================
// Details
// ===========================================================================

export function DeviceDetailPage() {
  const { id: idStr } = useParams({ strict: false }) as { id?: string };
  const id = Number(idStr);
  const canEdit = useCanEdit();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const { data: d, isLoading, error } = useQuery({
    queryKey: ['device', id],
    queryFn: () => devices.get(id),
    enabled: Number.isFinite(id),
  });
  const retire = useMutation({
    mutationFn: (active: boolean) => devices.update(id, { active }),
    onSuccess: () => invalidateAssets(queryClient),
  });

  if (isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (error) return <p className="text-sm text-red-600">{(error as Error).message}</p>;
  if (!d) return <p className="text-sm text-slate-500">Not found</p>;
  const incomplete = !d.make || !d.model || !d.ownership_name || !d.owner_display;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Link to="/assets/phones" className="text-sm text-brand-700 hover:underline">
            ← Phones
          </Link>
          <h1 className="text-2xl font-semibold text-slate-900 font-mono">
            {d.device_identifier}
            {!d.active && <span className="ml-2 text-base text-slate-500 font-sans">(retired)</span>}
          </h1>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button onClick={() => setEditing(true)}>Edit</Button>
            <Button variant="secondary" disabled={retire.isPending} onClick={() => retire.mutate(!d.active)}>
              {d.active ? 'Retire' : 'Reactivate'}
            </Button>
          </div>
        )}
      </div>
      {retire.error && <p className="text-sm text-red-600">{(retire.error as Error).message}</p>}
      {incomplete && <IncompleteNote />}

      <Card>
        <CardHeader>
          <CardTitle>Device</CardTitle>
        </CardHeader>
        <CardBody>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-4 text-sm">
            <DeviceItems d={d} />
          </dl>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Assigned phone numbers</CardTitle>
        </CardHeader>
        <CardBody>
          {d.assigned_phones.length === 0 ? (
            <p className="text-sm text-slate-500">None. Add one with Edit, or from the phone number’s page.</p>
          ) : (
            <ul className="text-sm space-y-1">
              {d.assigned_phones.map((p) => (
                <li key={p.id}>
                  <Link to="/assets/phones/$id" params={{ id: String(p.id) }} className="font-mono text-brand-700 hover:underline">
                    {p.display}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <LinkedProfiles rows={d.linked_profiles} />
      <HistoryCard queryKey={['device', id, 'history']} load={() => devices.history(id)} />
      <DeviceFormDialog open={editing} onClose={() => setEditing(false)} device={d} onSaved={() => undefined} />
    </div>
  );
}

export function PhoneDetailPage() {
  const { id: idStr } = useParams({ strict: false }) as { id?: string };
  const id = Number(idStr);
  const canEdit = useCanEdit();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [reassignTo, setReassignTo] = useState<number | null | undefined>(undefined);
  const { data: p, isLoading, error } = useQuery({
    queryKey: ['phone-number', id],
    queryFn: () => phoneNumbers.get(id),
    enabled: Number.isFinite(id),
  });
  const assign = useMutation({
    mutationFn: (deviceId: number | null) => phoneNumbers.assignDevice(id, deviceId),
    onSuccess: () => {
      setReassignTo(undefined);
      invalidateAssets(queryClient);
    },
  });
  const retire = useMutation({
    mutationFn: (active: boolean) => phoneNumbers.update(id, { active }),
    onSuccess: () => invalidateAssets(queryClient),
  });

  if (isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (error) return <p className="text-sm text-red-600">{(error as Error).message}</p>;
  if (!p) return <p className="text-sm text-slate-500">Not found</p>;
  const incomplete =
    !p.line_type ||
    !p.provider_name ||
    !p.payment_plan_name ||
    !p.owner_display ||
    !p.carrier_type_name ||
    !p.payment_responsibility_name ||
    !p.test_status_name ||
    !p.usage_status_name;
  const pending = reassignTo !== undefined && reassignTo !== p.device_id;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Link to="/assets/phones" className="text-sm text-brand-700 hover:underline">
            ← Phones
          </Link>
          <h1 className="text-2xl font-semibold text-slate-900 font-mono">
            {p.display}
            {!p.active && <span className="ml-2 text-base text-slate-500 font-sans">(retired)</span>}
          </h1>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button onClick={() => setEditing(true)}>Edit</Button>
            <Button variant="secondary" disabled={retire.isPending} onClick={() => retire.mutate(!p.active)}>
              {p.active ? 'Retire' : 'Reactivate'}
            </Button>
          </div>
        )}
      </div>
      {retire.error && <p className="text-sm text-red-600">{(retire.error as Error).message}</p>}
      {incomplete && <IncompleteNote />}

      <Card>
        <CardHeader>
          <CardTitle>Phone</CardTitle>
        </CardHeader>
        <CardBody>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-4 text-sm">
            <Item label="Phone Number" mono>{p.display}</Item>
            <Item label="Subscriber Name">{p.owner_display ?? '—'}</Item>
            <Item label="Line Type">{lineTypeLabel(p.line_type) ?? '—'}</Item>
            <Item label="Activation Date">{p.activation_date ?? '—'}</Item>
            <Item label="Carrier Name">{p.provider_name ?? '—'}</Item>
            <Item label="Carrier Type">{p.carrier_type_name ?? '—'}</Item>
            <Item label="Payment Plan">{p.payment_plan_name ?? '—'}</Item>
            <Item label="Payment Responsibility">{p.payment_responsibility_name ?? '—'}</Item>
            <Item label="Test Status">{p.test_status_name ?? '—'}</Item>
            <Item label="Usage Status">{p.usage_status_name ?? '—'}</Item>
            <Item label="FRT Notes">{p.notes ?? '—'}</Item>
          </dl>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Device</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3 text-sm">
          {p.device_id && <PhoneDeviceDetails deviceId={p.device_id} />}
          <p>
            Currently on:{' '}
            {p.device_id ? (
              <Link to="/assets/devices/$id" params={{ id: String(p.device_id) }} className="text-brand-700 hover:underline">
                {p.device_display}
              </Link>
            ) : (
              <span className="text-slate-500">No device</span>
            )}
          </p>
          {canEdit && (
            <div className="flex flex-wrap items-end gap-2">
              <label className="block min-w-64 flex-1">
                <span className="block text-slate-600">Move to</span>
                <DevicePicker
                  value={reassignTo === undefined ? p.device_id : reassignTo}
                  onChange={setReassignTo}
                  noneLabel="No device"
                />
              </label>
              <Button
                disabled={!pending || assign.isPending}
                onClick={() => assign.mutate(reassignTo ?? null)}
              >
                {assign.isPending ? 'Saving…' : 'Reassign'}
              </Button>
              {pending && (
                <Button variant="ghost" onClick={() => setReassignTo(undefined)}>
                  Cancel
                </Button>
              )}
            </div>
          )}
          {assign.error && <p className="text-red-600">{(assign.error as Error).message}</p>}
        </CardBody>
      </Card>

      <PortingCard phone={p} canEdit={canEdit} />
      <LinkedProfiles rows={p.linked_profiles} />
      <HistoryCard queryKey={['phone-number', id, 'history']} load={() => phoneNumbers.history(id)} />
      <PhoneFormDialog open={editing} onClose={() => setEditing(false)} phone={p} onSaved={() => undefined} />
    </div>
  );
}

// ===========================================================================
// Shared bits
// ===========================================================================

/** Device attributes (mig 0020), shared by the device page and a phone's
 *  Device card. */
function DeviceItems({ d }: { d: DeviceRow }) {
  const location = [d.location_city, d.location_region].filter(Boolean).join(', ');
  return (
    <>
      <Item label="Device ID" mono>
        {d.device_identifier}
      </Item>
      <Item label="Phone Manufacturer">{d.make ?? '—'}</Item>
      <Item label="Phone Model">{d.model ?? '—'}</Item>
      <Item label="Operating System">{d.os_name ?? '—'}</Item>
      <Item label="Carrier / SIM Restriction">{d.sim_restriction_name ?? '—'}</Item>
      <Item label="Jailbroken / Rooted">{d.root_status_name ?? '—'}</Item>
      <Item label="Device Ownership">{d.ownership_name ?? '—'}</Item>
      <Item label="Device Custodian">{d.owner_display ?? '—'}</Item>
      <Item label="Location of Device">{location || '—'}</Item>
      <Item label="Device notes">{d.notes ?? '—'}</Item>
    </>
  );
}

function PhoneDeviceDetails({ deviceId }: { deviceId: number }) {
  const { data: d } = useQuery({
    queryKey: ['device', deviceId],
    queryFn: () => devices.get(deviceId),
  });
  if (!d) return null;
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-4 rounded bg-slate-50 p-3 text-sm">
      <DeviceItems d={d} />
    </dl>
  );
}

/** Porting history (mig 0020): ports newest first, plus a form to record
 *  one. Recording the latest port updates the phone's carrier. */
function PortingCard({ phone, canEdit }: { phone: PhoneNumberDetail; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { data: carriers } = useQuery({
    queryKey: ['lookups', 'phone-provider-options'],
    queryFn: () => lookups.phoneProviderOptions.list(),
  });
  const [portDate, setPortDate] = useState('');
  const [toCarrier, setToCarrier] = useState(0);
  const [note, setNote] = useState('');
  const add = useMutation({
    mutationFn: () =>
      phoneNumbers.addPort(phone.id, {
        port_date: portDate,
        to_carrier_id: toCarrier,
        note: note.trim() || null,
      }),
    onSuccess: () => {
      setPortDate('');
      setToCarrier(0);
      setNote('');
      invalidateAssets(queryClient);
    },
  });
  const options = (carriers?.data ?? []).filter((c) => c.active && c.id !== phone.provider_id);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Porting history</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3 overflow-x-auto">
        {phone.ports.length === 0 ? (
          <p className="text-sm text-slate-500">No ports recorded.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500">
              <tr>
                <Th>Port Date</Th>
                <Th>From</Th>
                <Th>To</Th>
                <Th>Note</Th>
                <Th>Recorded by</Th>
              </tr>
            </thead>
            <tbody>
              {phone.ports.map((e) => (
                <tr key={e.id} className="border-t border-slate-100">
                  <Td>{e.port_date}</Td>
                  <Td>{e.from_carrier_name ?? '—'}</Td>
                  <Td>{e.to_carrier_name}</Td>
                  <Td>{e.note ?? ''}</Td>
                  <Td>{e.created_by_username ?? '—'}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {canEdit && (
          <form
            className="flex flex-wrap items-end gap-2 border-t border-slate-200 pt-3 text-sm"
            onSubmit={(e) => {
              e.preventDefault();
              if (portDate && toCarrier) add.mutate();
            }}
          >
            <label className="block">
              <span className="block text-slate-600">Port date</span>
              <Input type="date" value={portDate} onChange={(e) => setPortDate(e.target.value)} />
            </label>
            <span className="pb-2 text-slate-600">
              from <strong>{phone.provider_name ?? '—'}</strong> to
            </span>
            <label className="block">
              <span className="block text-slate-600">New carrier</span>
              <select
                value={toCarrier}
                onChange={(e) => setToCarrier(Number(e.target.value))}
                className="rounded border border-slate-300 bg-white px-2 py-2 text-sm"
              >
                <option value={0}>— select —</option>
                {options.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block flex-1 min-w-48">
              <span className="block text-slate-600">Note</span>
              <Input value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            <Button type="submit" disabled={!portDate || !toCarrier || add.isPending}>
              {add.isPending ? 'Saving…' : 'Record port'}
            </Button>
            {add.error && <p className="basis-full text-red-600">{(add.error as Error).message}</p>}
            <p className="basis-full text-xs text-slate-500">
              The most recent port sets the phone’s current carrier.
            </p>
          </form>
        )}
      </CardBody>
    </Card>
  );
}

function invalidateAssets(queryClient: ReturnType<typeof useQueryClient>) {
  for (const key of ['devices', 'device', 'phone-numbers', 'phone-number']) {
    void queryClient.invalidateQueries({ queryKey: [key] });
  }
}

export function HistoryCard({
  queryKey,
  load,
}: {
  queryKey: unknown[];
  load: () => Promise<{ data: AssetHistoryEntry[] }>;
}) {
  const { data, isLoading } = useQuery({ queryKey, queryFn: load });
  const rows = data?.data ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Change history</CardTitle>
      </CardHeader>
      <CardBody className="overflow-x-auto">
        {isLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500">No changes recorded.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500">
              <tr>
                <Th>When</Th>
                <Th>Changed by</Th>
                <Th>Change</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((h) => (
                <tr key={h.id} className="border-t border-slate-100 align-top">
                  <Td>{new Date(h.at).toLocaleString()}</Td>
                  <Td>{h.actor_username ?? <span className="text-slate-500">system</span>}</Td>
                  <Td>
                    {h.note && <div className="text-slate-600">{h.note}</div>}
                    {h.action === 'create' && !h.note && <div className="font-medium">Created</div>}
                    {h.action !== 'create' && h.changes.length === 0 && !h.note && (
                      <span className="text-slate-500">Saved without changes</span>
                    )}
                    <ul className="space-y-0.5">
                      {h.changes.map((c) => (
                        <li key={c.field}>
                          <span className="text-slate-500">{c.field}:</span>{' '}
                          {h.action === 'create' ? (
                            c.after ?? '—'
                          ) : (
                            <>
                              <span className="line-through text-slate-400">{c.before ?? '—'}</span>
                              {' → '}
                              <span>{c.after ?? '—'}</span>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

function LinkedProfiles({ rows }: { rows: AssetLinkedProfile[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Used on online profiles</CardTitle>
      </CardHeader>
      <CardBody>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-500">Not used on any online profile.</p>
        ) : (
          <ul className="text-sm space-y-1">
            {rows.map((r, i) => (
              <li key={`${r.customer_profile_id}-${i}`}>
                <Link
                  to="/profiles/$id"
                  params={{ id: String(r.customer_profile_id) }}
                  className="text-brand-700 hover:underline"
                >
                  {r.display}
                </Link>{' '}
                <span className="text-slate-500">
                  — {r.usage} · {r.account_count} account{r.account_count === 1 ? '' : 's'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function ListFilters({
  q,
  onQ,
  active,
  onActive,
  placeholder,
}: {
  q: string;
  onQ: (v: string) => void;
  active: ActiveFilter;
  onActive: (v: ActiveFilter) => void;
  placeholder: string;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      <Input value={q} onChange={(e) => onQ(e.target.value)} placeholder={placeholder} className="max-w-xs" />
      <select
        value={active}
        onChange={(e) => onActive(e.target.value as ActiveFilter)}
        className="rounded border border-slate-300 bg-white px-2 py-2 text-sm"
      >
        <option value="true">Active</option>
        <option value="false">Retired</option>
        <option value="all">All</option>
      </select>
    </div>
  );
}

function IncompleteNote() {
  return (
    <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
      Some required details are missing (this record was converted from earlier account data). Use
      Edit to complete it.
    </p>
  );
}

function lineTypeLabel(v: string | null): string | null {
  return PHONE_LINE_TYPES.find((t) => t.value === v)?.label ?? null;
}

function Missing() {
  return <span className="text-amber-600">missing</span>;
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}

function Td({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  return <td className={`px-3 py-2 ${mono ? 'font-mono' : ''}`}>{children}</td>;
}

function Item({ label, mono, children }: { label: string; mono?: boolean; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-2 gap-x-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className={mono ? 'font-mono' : undefined}>{children}</dd>
    </div>
  );
}
